/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { MenuItem } from "./MenuItem";
import { css } from "@emotion/react";
import { COOL_BLUE, COOL_GREY, COOLER_GREY, WHITE } from "../styles/colors";
import styled from "@emotion/styled";
import { getResource } from "../language/resources/getResource";
import { DROP_SHADOW, UNSELECTABLE_TEXT } from "../styles/styles";
import { FADE_IN_ANIMATION } from "../styles/animations";
import arrowIcon from '../assets/icons/downArrow.svg';

const SIDEBAR_EXPANDED_WIDTH = 175;
const SIDEBAR_COLLAPSED_WIDTH = 60;

const styles = {
  sidebar: css`
    background-color: ${WHITE};
    border: solid ${COOL_GREY} 1px;
    ${FADE_IN_ANIMATION};
    ${DROP_SHADOW};
    width: ${SIDEBAR_EXPANDED_WIDTH}
    color: black;
    overflow-y: auto;
    position: relative;    
    z-index: 2                  /* stack above main content */
    box-sizing: border-box;

    /* EXPANDED: allow stretching */
    transition: width 0.25s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
  `,

  sidebarCollapsed: css`
    /* COLLAPSED: stop stretching */
    height: auto;
    width: ${SIDEBAR_COLLAPSED_WIDTH}
    background-color: transparent;
    align-self: flex-start;
    overflow: hidden;                 /* Hide menu */
  `,

  collapseBtn: css`
    width: 100%;
    background-color: ${COOL_GREY};
    color: black;
    border: none;
    padding: 5px;
    cursor: pointer;
    display: flex;
    align-items: right;
    justify-content: right;

    &:hover {
      background: ${COOLER_GREY};     
    }

    &.collapsed {
      justify-content: center;   /* Center icon when collapsed */
    }
  `,

  menuContainer: css`
    display: flex;
    flex-direction: column;;
  `
};

const itemWrapper = css`
  &.root-item:first-of-type {
    margin-top: 8px;
  }

  &:last-of-type {
    margin-bottom: 8px;
  }
`;

const itemBase = css`
  font: inherit;
  line-height: inherit;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;            /* fill the entire column width */
  box-sizing: border-box; 
  padding: 8px 10px;       
  text-decoration: none;
  color: inherit;
  background: transparent;
  cursor: pointer;

  &:hover {
    background: ${COOL_BLUE};     
  };

  &.active {
    background: ${COOL_BLUE};      
  };
`;

const resetButton = css`
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  background: none;
  padding: 0;
  margin: 0;

  font: inherit;
  line-height: inherit;
  color: inherit;
`;


const ItemLink = styled(NavLink)`
  ${itemBase};
  border: none;  
  font: inherit;
  line-height: inherit;
`;

const ItemButton = styled("button")`
  ${resetButton};
  ${itemBase};
  border: none;
  background: transparent;
  text-align: left;
`;

const leftArrow  = css`transform: rotate(90deg);`;
const rightArrow = css`transform: rotate(-90deg);`;

const menuToggleArrow = (isCollapsed) => css`
  width: 25px;
  height: 25px;
  ${UNSELECTABLE_TEXT};

  ${isCollapsed
    ? css`margin-right: 0; ${rightArrow};`
    : css`margin-right: 5px; ${leftArrow};`}
`;

const parentItemArrow = (isOpen: boolean) => css`
  width: 20px;
  height: 20px;
  margin-left: 8px;

  ${isOpen 
    ? css`transform: rotate(180deg);`   
    : css`transform: rotate(0deg);`    
  };

  transition: width 0.25s ease;
  ${UNSELECTABLE_TEXT};
`;


interface SidebarMenuProps {
  items: MenuItem[];
  userRole: string;
  resources: Record<string, any>;
}

export const SidebarMenu: React.FC<SidebarMenuProps> = ({ items, userRole, resources }) => {
  const [collapsed, setCollapsed] = useState(false);

  const getDefaultOpenIds = (items: MenuItem[]): string[] => {
    const result: string[] = [];

    const traverse = (list: MenuItem[]) => {
      for (const item of list) {
        if (item.children && item.children.length > 0) {
          result.push(item.id);
          traverse(item.children);
        }
      }
    };

    traverse(items);
    return result;
  };

  const [openIds, setOpenIds] = useState<string[]>(() => getDefaultOpenIds(items));

  const getLabel = (item: MenuItem) =>
    item.labelKey ? String(resources[item.labelKey] ?? item.labelKey) : (item.label ?? "");

  const toggleOpen = (id: string) => {
    setOpenIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const renderItems = (menu: MenuItem[], level = 0) =>
    menu
      .filter((item) => item.roles.includes(userRole))
      .map((item) => {
        const hasChildren = item.children && item.children.length > 0;
        const isOpen = openIds.includes(item.id);
        const indent = level * 16 + 15;

        return (
          <div 
            key={item.id} 
            css={itemWrapper}
            className={level === 0 ? "root-item" : ""}
          >
            {hasChildren ? (
              <ItemButton
                onClick={() => toggleOpen(item.id)}
                style={{ paddingLeft: indent }}
                aria-expanded={isOpen}
              >
                {!collapsed && (
                  <>
                    <span>{getLabel(item)}</span>
                    <img
                      src={arrowIcon}
                      alt=""
                      css={parentItemArrow(isOpen)}
                    />
                  </>
                )}
              </ItemButton>
            ) : (
              <ItemLink
                to={item.path!}
                className={({ isActive }) => (isActive ? "active" : "")}
                style={{ paddingLeft: indent }}
              >
                {!collapsed && getLabel(item)}
              </ItemLink>
            )}

            {hasChildren && isOpen && !collapsed && renderItems(item.children!, level + 1)}
          </div>
        );
      });

  return (
    <div css={[styles.sidebar, collapsed && styles.sidebarCollapsed]}>
      <button
        css={styles.collapseBtn}
        className={collapsed ? "collapsed" : ""}
        onClick={() => setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
        title={
          collapsed
            ? getResource(resources, "PRODUCT_ADMIN_OPEN_NAVIGATION_PANEL")
            : getResource(resources, "PRODUCT_ADMIN_CLOSE_NAVIGATION_PANEL")
        }
      >
        <img alt='menu' src={arrowIcon} css={menuToggleArrow(collapsed)} />
      </button>

      {/* Only render menu when expanded */}
      {!collapsed && <div css={styles.menuContainer}>{renderItems(items)}</div>}
    </div>
  );
};
